/** @jsxImportSource @emotion/react */

import { ApolloError, useMutation } from '@apollo/client'
import { useMemo } from 'react'
import { BLOG_POST_MEDIA_PATH_S3 } from 'constants/images'
import { getTableOfContent, validateTableOfContent } from 'components/TableOfContent'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { prepareEditorHtml } from 'components/Editor'
import { useImageUpload } from 'utils/saveImageS3'
import { CREATE_SUCCESS_STORY } from 'queries/successStory'
import { createSuccessStory, createSuccessStoryVariables } from '__generatedTypes__/createSuccessStory'
import { PageLoader } from 'components/PageLoader'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { toast } from 'utils/toast'
import * as classes from './styles'
import { BlogPostType, Form, VALIDATE_TABLE_OF_CONTENT_OPTIONS } from './Form'
import { FormValues } from './types'
import { useGetSimilarPostsInitialValue } from './hooks'

export const REFETCH_SUCCESS_STORIES_MESSAGE = 'refetch-success-stories'
const BAD_SLUG_SUCCESS_STORY_ERROR_MESSAGE = 'Success Story with this slug already created :('
const BAD_SLUG_STUDY_GUIDE_ERROR_MESSAGE = 'Study Guide with this slug already created :('
const BAD_SLUG_STRATEGY_ERROR_MESSAGE = 'Strategy with this slug already created :('

export const prepareHtmlForSubmit = async (values: Partial<FormValues>): Promise<string> => {
  const storyAsHtml = await prepareEditorHtml(values.contentAsHtml ?? '', BLOG_POST_MEDIA_PATH_S3)

  return storyAsHtml
}

export const handleServerErrors = (error: unknown) => {
  if (error && typeof error === 'object' && 'graphQLErrors' in error) {
    const existingSlugErrorIndex = (error.graphQLErrors as ApolloError['graphQLErrors']).findIndex(({ message }) =>
      [
        BAD_SLUG_SUCCESS_STORY_ERROR_MESSAGE,
        BAD_SLUG_STUDY_GUIDE_ERROR_MESSAGE,
        BAD_SLUG_STRATEGY_ERROR_MESSAGE,
      ].includes(message)
    )

    if (existingSlugErrorIndex !== -1) {
      return { slug: 'Post with this slug already exists' }
    }
  }
}

type Props = {
  platform: Platform
}

export const Create = withPlatformCheck(({ platform }: Props) => {
  const uploadImage = useImageUpload()
  const bc = useBroadcastChannel()

  const { loading, getInitialValue } = useGetSimilarPostsInitialValue(platform.id)

  const [createSuccessStory] = useMutation<createSuccessStory, createSuccessStoryVariables>(CREATE_SUCCESS_STORY)

  const handleSubmit = async (values: FormValues) => {
    try {
      const {
        title,
        description = '',
        featuredArticleDescription,
        slug,
        withWhiteImageBG = false,
        score,
        fullName,
      } = values
      const tableOfContent = getTableOfContent(values.contentAsHtml)
      const { error } = validateTableOfContent(tableOfContent, VALIDATE_TABLE_OF_CONTENT_OPTIONS)

      if (error) {
        return
      }

      const storyAsHtml = await prepareHtmlForSubmit(values)

      const coverImageFile = values.coverImage?.[0]?.file

      const coverImage = await (coverImageFile
        ? uploadImage(coverImageFile, BLOG_POST_MEDIA_PATH_S3)
        : Promise.resolve(''))

      const authorId = values.author[0].id || ''

      await createSuccessStory({
        variables: {
          createSuccessStoryInput: {
            title,
            description,
            featuredArticleDescription,
            slug,
            score,
            fullName,
            author: {
              id: authorId,
            },
            storyAsHtml,
            withWhiteImageBG,
            coverImage,
            tableOfContent,
            platform: {
              id: platform.id,
            },
            studentsLikeYou: values.similarPosts?.map(({ id }) => id),
          },
        },
      })

      bc.postMessage(REFETCH_SUCCESS_STORIES_MESSAGE)

      window.close()
    } catch (error: unknown) {
      toast.error('Failed to create success Story')

      return handleServerErrors(error)
    }
  }

  const initialValues = useMemo(
    () => ({
      similarPosts: getInitialValue([]),
    }),
    [loading]
  )

  if (loading) {
    return <PageLoader />
  }

  return (
    <section css={classes.container}>
      <Form
        initialValues={initialValues}
        blogPostType={BlogPostType.SuccessStory}
        platform={platform}
        onSubmit={handleSubmit}
      />
    </section>
  )
})
